/**
 * «Ricette, fogli e foto» (DEC-23). Lei fotografa (o sceglie dalla galleria) una ricetta, un
 * foglio o una foto; mia la conserva cifrata e gliela rilegge. La foto resta la fonte: sui
 * fogli mia scrive solo di che cosa si tratta, niente dosi o codici (DEC-17).
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  ChoiceChip,
  ErrorMessage,
  PrimaryButton,
  ProgressWords,
  QuestionTitle,
  SecondaryButton,
  WhyText,
  WizardFrame,
} from '../components'
import type { ApiError } from '@mamma/contracts'
import { ApiFailure } from '../api/client'
import { documentiApi, preparaFoto, type Documento, type TipoDocumento } from '../api/documenti'

const TIPI: { kind: TipoDocumento; label: string; nome: string }[] = [
  { kind: 'ricetta_cucina', label: 'Una ricetta di cucina', nome: 'Ricetta' },
  { kind: 'foglio', label: 'Un foglio (anche del medico)', nome: 'Foglio' },
  { kind: 'foto', label: 'Una foto a cui tiene', nome: 'Foto' },
  { kind: 'altro', label: 'Altro', nome: 'Altro' },
]
const NOME = Object.fromEntries(TIPI.map((t) => [t.kind, t.nome])) as Record<TipoDocumento, string>
const DATA = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'long' })

type Fase =
  | { v: 'elenco' }
  | { v: 'scegli' }
  | { v: 'invio' }
  | { v: 'dettaglio'; doc: Documento; foto: string | null }

function comeErrore(problema: unknown): ApiError {
  return problema instanceof ApiFailure
    ? problema.error
    : { code: 'imprevisto', message_it: 'Non sono riuscita a usare questa foto.', remedy_it: 'La riprovi a scattare.' }
}

export function DocumentiRoute() {
  const [fase, setFase] = useState<Fase>({ v: 'elenco' })
  const [docs, setDocs] = useState<Documento[] | null>(null)
  const [errore, setErrore] = useState<ApiError | null>(null)
  const [busy, setBusy] = useState(false)
  const [togli, setTogli] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const tipo = useRef<TipoDocumento>('ricetta_cucina')

  const carica = useCallback(async () => {
    try {
      setDocs((await documentiApi.list()).documents)
      setErrore(null)
    } catch (problema) {
      setErrore(comeErrore(problema))
    }
  }, [])

  useEffect(() => {
    void carica()
  }, [carica])

  const apri = async (id: string) => {
    setBusy(true)
    setTogli(false)
    try {
      const [{ document }, file] = await Promise.all([documentiApi.get(id), documentiApi.file(id)])
      setFase({ v: 'dettaglio', doc: document, foto: `data:${file.media_type};base64,${file.data_b64}` })
    } catch (problema) {
      setErrore(comeErrore(problema))
    } finally {
      setBusy(false)
    }
  }

  const scegliTipo = (kind: TipoDocumento) => {
    tipo.current = kind
    input.current?.click()
  }

  const inviaFoto = async (file: File | undefined) => {
    if (!file) return
    setFase({ v: 'invio' })
    setErrore(null)
    try {
      const foto = await preparaFoto(file)
      const { document } = await documentiApi.create({ kind: tipo.current, ...foto })
      setFase({ v: 'dettaglio', doc: document, foto: `data:${foto.media_type};base64,${foto.data_b64}` })
      void carica()
    } catch (problema) {
      setErrore(comeErrore(problema))
      setFase({ v: 'scegli' })
    } finally {
      if (input.current) input.current.value = ''
    }
  }

  const rileggi = async (doc: Documento, foto: string | null) => {
    setBusy(true)
    try {
      const { document } = await documentiApi.read(doc.id)
      setFase({ v: 'dettaglio', doc: document, foto })
    } catch (problema) {
      setErrore(comeErrore(problema))
    } finally {
      setBusy(false)
    }
  }

  const rimuovi = async (doc: Documento) => {
    setBusy(true)
    try {
      await documentiApi.remove(doc.id)
      await carica()
      setFase({ v: 'elenco' })
    } catch (problema) {
      setErrore(comeErrore(problema))
    } finally {
      setBusy(false)
      setTogli(false)
    }
  }

  const band = <ProgressWords block="Ricette, fogli e foto" progress="Le sue cose" />
  const fileInput = (
    <input
      ref={input}
      type="file"
      accept="image/*"
      className="hidden"
      data-testid="scegli-foto"
      onChange={(e) => void inviaFoto(e.target.files?.[0])}
    />
  )

  if (fase.v === 'invio') {
    return (
      <WizardFrame band={band}>
        <QuestionTitle>Sto guardando la foto…</QuestionTitle>
        <WhyText>Ci vuole qualche secondo. La foto è già al sicuro.</WhyText>
      </WizardFrame>
    )
  }

  if (fase.v === 'scegli') {
    return (
      <WizardFrame band={band}>
        {fileInput}
        <QuestionTitle>Che cosa vuole fotografare?</QuestionTitle>
        <WhyText>Poi può scattare la foto o sceglierne una già fatta.</WhyText>
        <div className="mt-4 flex flex-col items-start gap-3">
          {TIPI.map((t) => (
            <ChoiceChip key={t.kind} label={t.label} onSelect={() => scegliTipo(t.kind)} />
          ))}
        </div>
        {errore && <ErrorMessage error={errore} onRetry={() => setErrore(null)} retryLabel="Va bene" />}
        <div className="mt-auto pt-5">
          <SecondaryButton onClick={() => setFase({ v: 'elenco' })}>Torna indietro</SecondaryButton>
        </div>
      </WizardFrame>
    )
  }

  if (fase.v === 'dettaglio') {
    const { doc, foto } = fase
    return (
      <WizardFrame band={band}>
        <QuestionTitle>{doc.title || NOME[doc.kind]}</QuestionTitle>
        {foto && <img src={foto} alt={doc.title || NOME[doc.kind]} className="mt-3 w-full rounded-card" />}
        {doc.read_status === 'letto' && doc.read_text && (
          <p className="mt-4 whitespace-pre-line text-body text-ink" data-testid="testo-letto">
            {doc.read_text}
          </p>
        )}
        {doc.read_status !== 'letto' && (
          <>
            <WhyText>Non sono riuscita a leggerla adesso. La foto è salvata e la trova qui.</WhyText>
            <div className="mt-3">
              <SecondaryButton disabled={busy} onClick={() => void rileggi(doc, foto)}>
                Riprova a leggerla
              </SecondaryButton>
            </div>
          </>
        )}
        {errore && <ErrorMessage error={errore} onRetry={() => setErrore(null)} retryLabel="Va bene" />}
        <div className="mt-auto flex flex-col gap-3 pt-5">
          <PrimaryButton onClick={() => setFase({ v: 'elenco' })}>Torna all'elenco</PrimaryButton>
          {togli ? (
            <div className="flex gap-2.5">
              <SecondaryButton grow disabled={busy} onClick={() => void rimuovi(doc)}>
                Sì, la tolgo
              </SecondaryButton>
              <SecondaryButton grow onClick={() => setTogli(false)}>
                No, la tengo
              </SecondaryButton>
            </div>
          ) : (
            <SecondaryButton onClick={() => setTogli(true)}>Togli questa foto</SecondaryButton>
          )}
        </div>
      </WizardFrame>
    )
  }

  return (
    <WizardFrame band={band}>
      <QuestionTitle>Ricette, fogli e foto</QuestionTitle>
      <WhyText>Fotografi una ricetta, un foglio o una foto: la conservo io e gliela rileggo quando vuole.</WhyText>
      {errore && <ErrorMessage error={errore} onRetry={() => void carica()} />}
      {docs && docs.length === 0 && <p className="mt-4 text-body text-ink">Qui non c'è ancora niente.</p>}
      {docs && docs.length > 0 && (
        <ul className="mt-4 flex flex-col gap-3" aria-label="Le sue ricette, i fogli e le foto">
          {docs.map((d) => (
            <li key={d.id}>
              <SecondaryButton disabled={busy} onClick={() => void apri(d.id)}>
                {`${d.title || NOME[d.kind]} · ${NOME[d.kind]}, ${DATA.format(new Date(d.created_at))}`}
              </SecondaryButton>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-auto flex flex-col gap-3 pt-5">
        <PrimaryButton onClick={() => setFase({ v: 'scegli' })}>Fotografa qualcosa</PrimaryButton>
        <SecondaryButton onClick={() => window.location.assign('/oggi')}>Torna alla sua giornata</SecondaryButton>
      </div>
    </WizardFrame>
  )
}
